/** An outline's read helpers over the page draft (the tour plan §5, D10): no store of its
 *  own — one undo stack, one dirty count, one restore, one 409 path with the rest of the
 *  record — but per-record subscriptions, so editing row A never re-renders row B.
 *
 *  - `useOutlineNode(rec)` — one row: its values (the draft's in edit mode, the stored ones
 *    in view mode, a new row's defaults), its entries. Built on `useDraftRecord`, so it
 *    re-renders only when THAT record's entries change.
 *  - `useOutlineField(rec, path)` — one path of one row, re-rendering only when that entry
 *    changes.
 *  - `useOutlineList(rec, path)` — a membership list of one record.
 *  - `useDraftSelector(select, keyOf)` — a derived value of the whole draft (the outline's
 *    rows, the validation issues) that re-renders its caller only when the value's key
 *    changes: a keystroke in a row's title moves no row, so the outline does not render.
 *  - `useOutlineSet()` — the one setter: a SET with the path's baseline (or a new row's
 *    default) as `from`, coalesced per focus session when given a token (`newSession`).
 *  - `useOutlineRun()` — runs a structural operation (`ops.ts`) against the draft as it
 *    stands at the click, or says why it was refused.
 *
 *  The context (`OutlineProvider`) carries what does not change per keystroke: the
 *  baselines, the owning record, the levels, the kind's membership lists, the mode and
 *  whether the operator may edit. A record kind with more to share (the tour's tenant,
 *  issues store) keeps its own context beside it. */
import { createContext, useCallback, useContext, useMemo, useRef, useSyncExternalStore, type ReactNode } from 'react';

import { useSnackbar } from '@/components/Snackbar';
import type { MembershipPaths } from '@/features/records/collections/membership';
import { parseRecordKey, type DraftAction, type DraftEntry, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraftRecord, useDraftStable } from '@/features/records/draft/DraftProvider';

import type { OutlineOpResult, OutlineOpsContext } from './ops';
import { outlineDefault, type Baselines, type Entries, type OutlineSpec } from './tree';

export interface OutlineContextValue {
  /** The stored values, path space (memoised on the record's version). */
  baselines: Baselines;
  /** The record holding the top level's list (`tour:42`, `itinerary:7`). */
  ownerRec: RecordKey;
  spec: OutlineSpec;
  /** The record kind's ONE membership set (see `OutlineOpsContext.memberships`). */
  memberships: MembershipPaths;
  /** Edit mode: views read the draft. */
  editing: boolean;
  /** The operator may change the outline; with `editing` the inputs are live. */
  canEdit: boolean;
}

const OutlineContext = createContext<OutlineContextValue | null>(null);

export function OutlineProvider({ value, children }: { value: OutlineContextValue; children: ReactNode }) {
  return <OutlineContext.Provider value={value}>{children}</OutlineContext.Provider>;
}

export function useOutline(): OutlineContextValue {
  const v = useContext(OutlineContext);
  if (!v) throw new Error('useOutline must be used inside <OutlineProvider>');
  return v;
}

const EMPTY: readonly number[] = Object.freeze([]);

export interface OutlineNode {
  rec: RecordKey;
  entity: string;
  id: number;
  /** A row the draft creates. */
  isNew: boolean;
  /** The row's entries (edit mode only). */
  entries: Readonly<Record<string, DraftEntry>> | undefined;
  /** What the views show: the draft's value in edit mode, else the stored one. */
  value: (path: string) => unknown;
  /** The stored value (a new row's default). */
  stored: (path: string) => unknown;
  entry: (path: string) => DraftEntry | undefined;
}

export function useOutlineNode(rec: RecordKey): OutlineNode {
  const entries = useDraftRecord(rec);
  const { baselines, editing, spec } = useOutline();
  const row = baselines[rec];
  return useMemo(() => {
    const { entity, id } = parseRecordKey(rec);
    const own = editing ? entries : undefined;
    const stored = (path: string) => {
      const v = row?.[path];
      return v === undefined ? outlineDefault(spec, rec, path) : v;
    };
    return {
      rec,
      entity,
      id,
      isNew: id < 0,
      entries: own,
      stored,
      value: (path: string) => {
        const e = own?.[path];
        return e ? e.to : stored(path);
      },
      entry: (path: string) => own?.[path],
    };
  }, [rec, entries, row, editing, spec]);
}

export function useOutlineList(rec: RecordKey, path: string): readonly number[] {
  const node = useOutlineNode(rec);
  const v = node.value(path);
  return useMemo(
    () => (Array.isArray(v) ? (v as unknown[]).filter((x): x is number => typeof x === 'number') : EMPTY),
    [v],
  );
}

/** A value derived from the whole draft (null entries in view mode), re-rendering only
 *  when `keyOf` of it changes. `select` and `keyOf` may be inline: the latest are used. */
export function useDraftSelector<T>(select: (entries: Entries | null) => T, keyOf: (value: T) => string): T {
  const { store } = useDraftStable();
  const { editing, baselines } = useOutline();
  const fns = useRef({ select, keyOf });
  fns.current = { select, keyOf };
  const cache = useRef<{ entries: unknown; editing: boolean; baselines: unknown; key: string; value: T } | null>(null);
  const getSnapshot = useCallback((): T => {
    const entries = store.getState().entries;
    const hit = cache.current;
    if (hit && hit.entries === entries && hit.editing === editing && hit.baselines === baselines) return hit.value;
    const value = fns.current.select(editing ? entries : null);
    const key = fns.current.keyOf(value);
    if (hit && hit.key === key) {
      cache.current = { ...hit, entries, editing, baselines };
      return hit.value;
    }
    cache.current = { entries, editing, baselines, key, value };
    return value;
  }, [store, editing, baselines]);
  return useSyncExternalStore(store.subscribe, getSnapshot);
}

export interface SetOptions {
  /** A focus session's token: its keystrokes undo as one step. */
  coalesce?: string | null;
  label?: string;
}

/** The one setter of an outline path (stable). */
export function useOutlineSet(): (rec: RecordKey, path: string, to: unknown, opts?: SetOptions) => void {
  const { dispatch, baselineOf } = useDraftStable();
  const { spec } = useOutline();
  return useCallback(
    (rec, path, to, opts) => {
      const stored = baselineOf(rec, path);
      dispatch({
        type: 'SET',
        changes: [{ rec, path, from: stored === undefined ? outlineDefault(spec, rec, path) : stored, to }],
        ...(opts?.label ? { label: opts.label } : {}),
        ...(opts?.coalesce ? { coalesce: opts.coalesce } : {}),
      });
    },
    [dispatch, baselineOf, spec],
  );
}

/** Dispatch a prepared action (a structural operation). Stable. */
export function useOutlineDispatch(): (action: DraftAction | null) => void {
  const { dispatch } = useDraftStable();
  return useCallback((action) => {
    if (action) dispatch(action);
  }, [dispatch]);
}

let sessionSeq = 0;

/** A new focus-session token for `rec|path`. */
export function newSession(rec: RecordKey, path: string): string {
  sessionSeq += 1;
  return `${rec}|${path}#outline${sessionSeq}`;
}

export interface OutlineField {
  /** What the views show (the draft's value in edit mode, the stored one in view mode). */
  value: unknown;
  /** The stored value (a new row's default). */
  stored: unknown;
  entry: DraftEntry | undefined;
}

/** One path of one row — re-renders only when THAT entry changes (a keystroke in a row's
 *  title renders the title, not the row's other inputs). */
export function useOutlineField(rec: RecordKey, path: string): OutlineField {
  const { store } = useDraftStable();
  const { baselines, editing, spec } = useOutline();
  const getSnapshot = useCallback(() => store.getState().entries[rec]?.[path], [store, rec, path]);
  const own = useSyncExternalStore(store.subscribe, getSnapshot);
  const raw = baselines[rec]?.[path];
  return useMemo(() => {
    const stored = raw === undefined ? outlineDefault(spec, rec, path) : raw;
    const entry = editing ? own : undefined;
    return { value: entry ? entry.to : stored, stored, entry };
  }, [own, raw, editing, rec, path, spec]);
}

export type OutlineOp = (ctx: OutlineOpsContext) => OutlineOpResult | null;

/** `run(op)` → the new row's id (an add), true (done), or null (nothing / refused — the
 *  refusal said in a snackbar). Reads the draft as it stands at the call. Stable per
 *  outline. */
export function useOutlineRun(): (op: OutlineOp) => number | true | null {
  const { store, dispatch } = useDraftStable();
  const { baselines, ownerRec, spec, memberships } = useOutline();
  const snackbar = useSnackbar();
  return useCallback(
    (op) => {
      const r = op({ state: store.getState(), baselines, ownerRec, spec, memberships });
      if (!r) return null;
      if ('refused' in r) {
        snackbar.show(r.refused, 'warning');
        return null;
      }
      dispatch(r.action);
      return r.id ?? true;
    },
    [store, dispatch, baselines, ownerRec, spec, memberships, snackbar],
  );
}
